/**
 * JourneyProgress — DRR-309 Phase 2, Phase 8
 *
 * Overall progress visualization with task counts.
 * Shows ring progress for compact mode, bar progress for full mode.
 *
 * Phase 8: Responsive breakpoints for mobile/tablet/desktop
 */

import React from 'react';
import {
  Group,
  Stack,
  Text,
  Progress,
  RingProgress,
  Paper,
  ThemeIcon,
  SimpleGrid,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconChecks, IconListCheck } from '@tabler/icons-react';
// Status constants for comparison (type sharing between packages pending)
const GqlInstanceStatus = {
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  PAUSED: 'PAUSED',
} as const;
import type { JourneyProgressProps } from '../types';

function getProgressColor(percent: number, status: string): string {
  if (status === GqlInstanceStatus.COMPLETED) return 'green';
  if (status === GqlInstanceStatus.FAILED) return 'red';
  if (status === GqlInstanceStatus.PAUSED) return 'yellow';
  if (percent >= 75) return 'green';
  if (percent >= 50) return 'blue';
  if (percent >= 25) return 'yellow';
  return 'gray';
}

export function JourneyProgress({
  progressPercent,
  totalTasks,
  completedTasks,
  status,
  compact = false,
}: Readonly<JourneyProgressProps>) {
  // Phase 8: Responsive breakpoints
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isTablet = useMediaQuery('(max-width: 1024px)');

  const color = getProgressColor(progressPercent, status);
  const pendingTasks = totalTasks - completedTasks;

  if (compact) {
    return (
      <Group gap="xs">
        <RingProgress
          size={40}
          thickness={4}
          roundCaps
          sections={[{ value: progressPercent, color }]}
          label={
            <Text ta="center" fw={700} size="xs">
              {Math.round(progressPercent)}%
            </Text>
          }
        />
        <Text size="xs" c="dimmed">
          {completedTasks}/{totalTasks}
        </Text>
      </Group>
    );
  }

  // Mobile layout: simplified with progress bar and inline counts
  if (isMobile) {
    return (
      <Paper p="sm" withBorder>
        <Stack gap="sm">
          {/* Progress bar with percentage */}
          <div>
            <Group justify="space-between" mb={4}>
              <Text size="xs" fw={500}>
                Progress
              </Text>
              <Text size="sm" fw={700} c={color}>
                {Math.round(progressPercent)}%
              </Text>
            </Group>
            <Progress
              value={progressPercent}
              color={color}
              size="md"
              radius="xl"
              animated={status === 'RUNNING' && progressPercent < 100}
            />
          </div>

          {/* Task counts - horizontal on mobile */}
          <Group justify="center" gap="lg">
            <Group gap={4}>
              <ThemeIcon size="sm" variant="light" color="green">
                <IconChecks size={14} />
              </ThemeIcon>
              <Text size="sm" fw={600}>
                {completedTasks}
              </Text>
              <Text size="xs" c="dimmed">
                done
              </Text>
            </Group>
            <Group gap={4}>
              <ThemeIcon size="sm" variant="light" color="blue">
                <IconListCheck size={14} />
              </ThemeIcon>
              <Text size="sm" fw={600}>
                {pendingTasks}
              </Text>
              <Text size="xs" c="dimmed">
                left
              </Text>
            </Group>
          </Group>
        </Stack>
      </Paper>
    );
  }

  // Tablet layout: 2-column grid for stats
  if (isTablet) {
    return (
      <Paper p="sm" withBorder>
        <Stack gap="sm">
          {/* Progress bar with percentage */}
          <div>
            <Group justify="space-between" mb="xs">
              <Text size="sm" fw={500}>
                Progress
              </Text>
              <Text size="md" fw={700} c={color}>
                {Math.round(progressPercent)}%
              </Text>
            </Group>
            <Progress
              value={progressPercent}
              color={color}
              size="lg"
              radius="xl"
              animated={status === 'RUNNING' && progressPercent < 100}
            />
          </div>

          {/* Task counts - grid on tablet */}
          <SimpleGrid cols={3} spacing="xs">
            <Stack align="center" gap={2}>
              <ThemeIcon size="md" variant="light" color="green">
                <IconChecks size={16} />
              </ThemeIcon>
              <Text size="lg" fw={700}>
                {completedTasks}
              </Text>
              <Text size="xs" c="dimmed">
                Completed
              </Text>
            </Stack>

            <Stack align="center" gap={2}>
              <ThemeIcon size="md" variant="light" color="blue">
                <IconListCheck size={16} />
              </ThemeIcon>
              <Text size="lg" fw={700}>
                {pendingTasks}
              </Text>
              <Text size="xs" c="dimmed">
                Remaining
              </Text>
            </Stack>

            <Stack align="center" gap={2}>
              <RingProgress
                size={40}
                thickness={4}
                roundCaps
                sections={[{ value: progressPercent, color }]}
                label={
                  <Text ta="center" fw={700} size="xs">
                    {totalTasks}
                  </Text>
                }
              />
              <Text size="xs" c="dimmed">
                Total
              </Text>
            </Stack>
          </SimpleGrid>
        </Stack>
      </Paper>
    );
  }

  // Desktop layout: full
  return (
    <Paper p="md" withBorder>
      <Stack gap="md">
        {/* Progress bar with percentage */}
        <div>
          <Group justify="space-between" mb="xs">
            <Text size="sm" fw={500}>
              Progress
            </Text>
            <Text size="lg" fw={700} c={color}>
              {Math.round(progressPercent)}%
            </Text>
          </Group>
          <Progress
            value={progressPercent}
            color={color}
            size="xl"
            radius="xl"
            animated={status === 'RUNNING' && progressPercent < 100}
          />
        </div>

        {/* Task counts */}
        <Group justify="space-around">
          <Stack align="center" gap={4}>
            <ThemeIcon size="lg" variant="light" color="green">
              <IconChecks size={20} />
            </ThemeIcon>
            <Text size="xl" fw={700}>
              {completedTasks}
            </Text>
            <Text size="xs" c="dimmed">
              Completed
            </Text>
          </Stack>

          <Stack align="center" gap={4}>
            <ThemeIcon size="lg" variant="light" color="blue">
              <IconListCheck size={20} />
            </ThemeIcon>
            <Text size="xl" fw={700}>
              {pendingTasks}
            </Text>
            <Text size="xs" c="dimmed">
              Remaining
            </Text>
          </Stack>

          <Stack align="center" gap={4}>
            <RingProgress
              size={50}
              thickness={5}
              roundCaps
              sections={[{ value: progressPercent, color }]}
              label={
                <Text ta="center" fw={700} size="xs">
                  {totalTasks}
                </Text>
              }
            />
            <Text size="xs" c="dimmed">
              Total
            </Text>
          </Stack>
        </Group>
      </Stack>
    </Paper>
  );
}

export default JourneyProgress;
